import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect, useCallback, useRef } from "react";
import { loadUserProfile } from "../../redux/actions/authActions";
import { fetchProfilPrestataire } from "../../redux/actions/profilActions";

const FormModalUpdatePrestaInfosEntreprise = ({ onClose }) => {
    const { token } = useSelector((state) => state.auth);
    const profilPrestataire = useSelector((state)=>state.profil.prestataire);

    const apiUrl = process.env.REACT_APP_API_URL;

    const [errors, setErrors] = useState({});
    const [updateSuccess, setUpdateSuccess] = useState(false);
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({
        typeCompany: profilPrestataire.statutPrestataire || "",
        secteurCompany: profilPrestataire.secteur?.idSecteur || "",
        raisonSociale: profilPrestataire.raisonSocialePrestataire || "",
        nonAssujettiTva: profilPrestataire.isExempteTva || false,
        siret: profilPrestataire.siretPrestataire || "",
        numTva: profilPrestataire.numTvaPrestataire || "",
    });
    const logoInputRef = useRef(null);
    const previewUrl = profilPrestataire?.logoPrestataire ? apiUrl + "/api/files/?categorie=logos&file=" + profilPrestataire?.logoPrestataire : "";
    const [logoPreview, setLogoPreview] = useState(
        previewUrl
    );
    const isValidSiret = (siret) => {
        if (!/^\d{14}$/.test(siret)) return false;
        let sum = 0;
        for (let i = 0; i < 14; i++) {
            let num = parseInt(siret[i], 10);
            if ((14 - i) % 2 === 0) num *= 2;   // norme Luhn correcte
            if (num > 9) num -= 9;
            sum += num;
        }
        return sum % 10 === 0;
    };

    const isValidTva = (tva) =>
        /^FR\d{2}\d{9}$/.test(tva.replace(/\s+/g, ''));

    const [loading, setLoading] = useState(false);

    const isStepValid =
        !errors.raisonSociale &&
        !errors.typeCompany &&
        !errors.siret &&
        (
            (formData.typeCompany === "micro-entrepreneur" && formData.nonAssujettiTva)
            || (!!formData.numTva && !errors.numTva)
        );

    const validateField = (name, value) => {
        let fieldErrors = { ...errors };

        switch (name) {
            case "raisonSociale":
                if (!value || value.length < 2)
                    fieldErrors.raisonSociale = "La raison sociale doit contenir au moins 2 caractères";
                else delete fieldErrors.raisonSociale;
                break;

            case "typeCompany":
                if (!value)
                    fieldErrors.typeCompany = "Le statut de l'entreprise est requis";
                else delete fieldErrors.typeCompany;
                break;

            case "siret":
                if (value && !isValidSiret(value))
                    fieldErrors.siret = "SIRET invalide";
                else delete fieldErrors.siret;
                break;

            case "numTva":
                if (formData.typeCompany === "micro-entrepreneur" && formData.nonAssujettiTva) {
                    delete fieldErrors.numTva; // non requis
                } else if (value && !isValidTva(value)) {
                    fieldErrors.numTva = "Numéro de TVA invalide";
                } else {
                    delete fieldErrors.numTva;
                }
                break;

            default:
                break;
        }

        setErrors(fieldErrors);
    };

    const [secteurs, setSecteurs] = useState([]);

    const refreshSecteurs = useCallback(() => {
        setLoading(true);

        fetch(`${apiUrl}/api/secteurs`)
            .then((res) => res.json())
            .then((data) => setSecteurs(data))
            .catch((err) => console.error(err))
            .finally(() => setLoading(false));
    }, [apiUrl]);

    useEffect(() => {
        refreshSecteurs();
    }, [refreshSecteurs]);

    const handleChange = (e) => {
        const { name, type, value, checked } = e.target;

        let newValue = type === "checkbox" ? checked : value;

        setFormData((prev) => ({
            ...prev,
            [name]: newValue,
        }));

        validateField(name, newValue);
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        if (type === "logoEntreprise") {
            setFormData((prev) => ({ ...prev, logoEntreprise: file }));
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleRemoveFile = (type) => {

        if (type === "logoEntreprise") {
            if (logoPreview.startsWith("blob:")) URL.revokeObjectURL(logoPreview);
            setFormData((p) => ({ ...p, logoEntreprise: null }));
            setLogoPreview(previewUrl);
            if (logoInputRef.current) logoInputRef.current.value = "";
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        if (Object.keys(errors).length > 0) {
            alert("Veuillez corriger les erreurs avant de soumettre.");
            return;
        }

        try {
            const form = new FormData();

            Object.keys(formData).forEach(key => {
                form.append(key, formData[key]);
            });

            const response = await fetch(`${apiUrl}/api/updateProfil/prestataire`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: form,
            });

            if (!response.ok) {
                const errorData = await response.json();
                alert(errorData.message || "Une erreur est survenue lors de la mise à jour.");
                return;
            }

            await response.json();
            setUpdateSuccess(true);
            dispatch(fetchProfilPrestataire(token));
            setTimeout(() => {
                onClose();
            }, 800);

        } catch (error) {
            console.error("Erreur lors de la mise à jour :", error);
            alert("Impossible de se connecter au serveur. Réessayez plus tard.");
        }
    };

    return (
        <div className="inscriptionModalOverlay">
            <div className="inscriptionModal">
                <button className="inscriptionModalClose" onClick={onClose}>×</button>
                <h3>Mon entreprise</h3>
                <form onSubmit={handleSubmit}>
                    <div className="inscriptionClientModal-input-form">
                        <label>Mon Logo</label>

                        {/* Input caché */}
                        <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={(e) => handleFileChange(e, "logoEntreprise")}
                        />

                        {logoPreview ? (
                            <div
                                style={{
                                    position: "relative",
                                    width: "120px",
                                    height: "120px",
                                    margin: "0 auto",
                                    cursor: "pointer",
                                }}
                            >
                                {/* IMAGE */}
                                <img
                                    src={logoPreview}
                                    alt="Preview"
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        borderRadius: "50%",
                                        objectFit: "cover",
                                    }}
                                    onClick={() => logoInputRef.current.click()}
                                />

                                {/* BOUTON CROIX */}
                                <div
                                    onClick={() => handleRemoveFile("logoEntreprise")}
                                    style={{
                                        position: "absolute",
                                        top: "-6px",
                                        right: "-6px",
                                        width: "26px",
                                        height: "26px",
                                        borderRadius: "50%",
                                        background: "#ff4d4d",
                                        color: "white",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        cursor: "pointer",
                                        fontWeight: "bold",
                                        fontSize: "18px",
                                        boxShadow: "0 0 5px rgba(0,0,0,0.3)",
                                        zIndex: 999,
                                    }}
                                >
                                    ×
                                </div>
                            </div>
                        ) : (
                            // L’input apparaît seulement si aucune photo
                            <button
                                type="button"
                                onClick={() => logoInputRef.current.click()}
                                className="btn-upload"
                            >
                                Choisir un logo
                            </button>
                        )}
                    </div>
                    {/* Raison sociale */}
                    <div className="inscriptionPrestataireModal-input-form">

                        <label>Raison sociale</label>
                        <input
                            type="text"
                            name="raisonSociale"
                            value={formData.raisonSociale}
                            onChange={handleChange}
                        />
                        {errors.raisonSociale && <p className="error-text">{errors.raisonSociale}</p>}
                    </div>

                    {/* Secteur */}
                    <div className="inscriptionPrestataireModal-input-form">
                        <label>Secteur d'activité de l'entreprise</label>
                        <select
                            name="secteurCompany"
                            value={formData.secteurCompany}
                            onChange={handleChange}
                            required
                        >
                            <option value="">-- Sélectionnez un secteur d'activité --</option>
                            {secteurs?.map((secteur) => (
                                <option key={secteur.idSecteur} value={secteur.idSecteur}>
                                    {secteur.labelSecteur}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Statut */}
                    <div className="inscriptionPrestataireModal-input-form">
                        <label>Statut de l'entreprise</label>
                        <select
                            name="typeCompany"
                            value={formData.typeCompany}
                            onChange={handleChange}
                            required
                        >
                            <option value="">-- Sélectionnez un statut --</option>

                            <option value="micro-entrepreneur">Micro-entrepreneur (ME)</option>
                            <option value="ei">Entreprise Individuelle (EI)</option>
                            <option value="eirl">EIRL</option>

                            <option value="sarl">SARL</option>
                            <option value="eurl">EURL</option>
                            <option value="sas">SAS</option>
                            <option value="sasu">SASU</option>
                            <option value="sa">SA</option>
                            <option value="snc">SNC</option>
                            <option value="scs">SCS</option>
                            <option value="sca">SCA</option>

                            <option value="autre">Autre</option>
                        </select>

                        {errors.typeCompany && <p className="error-text">{errors.typeCompany}</p>}
                    </div>

                    {/* Non assujetti */}
                    {formData.typeCompany === "micro-entrepreneur" && (
                        <div className="inscriptionPrestataireModal-input-form">
                            <label>
                                <input
                                    type="checkbox"
                                    name="nonAssujettiTva"
                                    checked={formData.nonAssujettiTva}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            nonAssujettiTva: e.target.checked,
                                            numTva: e.target.checked ? "" : prev.numTva,
                                        }))
                                    }
                                />
                                Non assujetti à la TVA (Article 293 B du CGI)
                            </label>
                        </div>
                    )}

                    {/* SIRET */}
                    <div className="inscriptionPrestataireModal-input-form">
                        <label>N° SIRET</label>
                        <input
                            type="text"
                            name="siret"
                            value={formData.siret}
                            onChange={handleChange}
                        />
                        {errors.siret && <p className="error-text">{errors.siret}</p>}
                    </div>

                    {/* TVA */}
                    {!(formData.typeCompany === "micro-entrepreneur" && formData.nonAssujettiTva) && (
                        <div className="inscriptionPrestataireModal-input-form">
                            <label>N° TVA Intracommunautaire</label>
                            <input
                                type="text"
                                name="numTva"
                                value={formData.numTva}
                                onChange={handleChange}
                            />
                            {errors.numTva && <p className="error-text">{errors.numTva}</p>}
                        </div>
                    )}

                    {/* Action */}
                    <div className="inscriptionPrestataireModal-actions">
                        <button
                            type="submit"

                            disabled={!isStepValid}
                        >
                            ENREGISTRER
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default FormModalUpdatePrestaInfosEntreprise;
